import {
  CredentialList,
  RegisterKeyButton,
  useAuthentication,
} from '@hurdl/authentication/frontend';
import { Button, Stack } from '@hurdl/design-system';
import { Link } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

/**
 * The properties of {@link AccountCredentialsPage}.
 *
 * The account whose keys the page administers, from the route path — a target, as the
 * capability's `CredentialList` takes one, not state the page owns.
 */
export interface AccountCredentialsPageProps {
  readonly userId: string;
}

/**
 * The per-account credential administration page: list an account's security keys,
 * disable one, and register a new one against the operator's own account (R9.4, R7.3,
 * R7.5, R1.6).
 *
 * It composes the Authentication_Capability's `CredentialList` for the named account and
 * `RegisterKeyButton` for a self-registration. The panel holds no store and reaches no
 * `fetch` of its own (panel invariants 1, 2): both components reach the API through the
 * capability's store and Generated_Client, and the administrative read and disable are
 * gated at the capability's routes.
 *
 * Registering a key and disabling one both change the list, so a successful act bumps a
 * key that remounts the `CredentialList`, which re-reads — the honest way to reflect a
 * change rather than patching a list the page does not hold. The `RegisterKeyButton`
 * registers against the **signed-in** account (the ceremony is bearer, binding to the
 * token's account), so it is shown only where the account being administered is the
 * operator's own; an administrator registering a key for another account uses the
 * capability's administrative enrolment, which this minimal page does not surface.
 *
 * The one `h1` is the page's; the `main` landmark is the Layout's.
 */
export function AccountCredentialsPage({
  userId,
}: AccountCredentialsPageProps): ReactElement {
  const authentication = useAuthentication();

  // A monotonic key the two acts bump, remounting the list so it re-reads.
  const [revision, setRevision] = useState(0);

  function reload(): void {
    setRevision((seen) => seen + 1);
  }

  const isOwnAccount = authentication.user?.id === userId;

  return (
    <Stack gap="large">
      <Stack gap="small">
        <Button variant="ghost" asChild>
          <Link to="/admin/accounts">Back to accounts</Link>
        </Button>
        <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
          Security keys
        </h1>
        <p className="text-hurdl-body text-hurdl-muted-foreground">
          The hardware keys registered on this account.
        </p>
      </Stack>

      {isOwnAccount ? (
        <Stack gap="medium">
          <h2 className="font-hurdl-display text-hurdl-subheading font-hurdl-semibold">
            Register a key
          </h2>
          <RegisterKeyButton onRegistered={reload} />
        </Stack>
      ) : null}

      <Stack gap="medium">
        <h2 className="font-hurdl-display text-hurdl-subheading font-hurdl-semibold">
          Registered keys
        </h2>
        <CredentialList key={revision} userId={userId} onDisabled={reload} />
      </Stack>
    </Stack>
  );
}
